import { config, GENDER, SIBLING_TYPE } from './config.js';

export class PedigreePerson {
  constructor(pid, data = {}) {
    this.pid = pid;
    this.upn = data.upn || 0;
    this.name = data.name || '';

    this.uid = data.uid;

    this.gender = data.gender || GENDER.unknown;
    this.sexAssignedAtBirth = data.sexAssignedAtBirth || GENDER.unknown;

    this.motherId = data.motherId || null;
    this.fatherId = data.fatherId || null;
    this.spouseIds = data.spouseIds || [];
    this.childrenIds = data.childrenIds || [];
    this.siblingIds = data.siblingIds || [];

    // === Twin/Multiple Birth Properties ===
    this.siblingType = data.siblingType || SIBLING_TYPE.normal;
    this.twinSubGroupId = data.twinSubGroupId || null;
    // Array of twin relationships: [{ withPid: number, type: 'monozygotic'|'dizygotic'|'unknown' }]
    this.twinRelations = data.twinRelations || [];

    this.virtualX = 0;
    this.virtualY = 0;
    this.rowDepth = 0;
    this.columnDepth = 0;

    this.x = 0;
    this.y = 0;

    this.primarySpouseId = null;
    this.spousesInOrder = [];
    this.orderRelativeToPrimarySpouse = 0;

    this.belongsToSiblingGroup = null;

    this.caption = data.caption || data.name || '';
    this.isHidden = data.isHidden || false;
    this.isDeceased = data.isDeceased || false;
    this.isFetus = data.isFetus || false;

    // === Reproductive nodes (Track A node-field contract; config.js §5.1) ===
    // Hoisted from `data` so the engine/render read them off the instance. Null/[] on
    // every existing person, and only consumed behind config.ENABLE_REPRO_NODES, so
    // layout is unchanged when the flag is off (or no node carries reproKind).
    this.reproKind = data.reproKind || null;
    this.pregnancy = data.pregnancy || null;
    this.donorType = data.donorType || null;
    this.donorOfPids = data.donorOfPids || [];

    this.kind = config.NODE_TYPE.NODE; // for d3 rendering
    this.data = data; // store original data
    this.isDynamicallyAdded = data.isDynamicallyAdded || false;

    this.family = null;
  }

  get mother() {
    if (!this.family || !this.motherId) return null;
    return this.family.getPersonById(this.motherId);
  }

  get father() {
    if (!this.family || !this.fatherId) return null;
    return this.family.getPersonById(this.fatherId);
  }

  get children() {
    if (!this.family) return [];
    return this.childrenIds
      .map((pid) => this.family.getPersonById(pid))
      .filter((p) => p !== null);
  }

  get spouses() {
    if (!this.family) return [];
    return this.spouseIds
      .map((pid) => this.family.getPersonById(pid))
      .filter((p) => p !== null);
  }

  get siblings() {
    if (!this.family) return [];
    return this.siblingIds
      .map((pid) => this.family.getPersonById(pid))
      .filter((p) => p !== null);
  }

  get leftParent() {
    if (!this.father && !this.mother) return null;
    if (!this.father) return this.mother;
    if (!this.mother) return this.father;

    return this.mother.virtualX < this.father.virtualX
      ? this.mother
      : this.father;
  }

  get rightParent() {
    if (!this.father && !this.mother) return null;
    if (!this.father) return this.mother;
    if (!this.mother) return this.father;

    return this.father.virtualX < this.mother.virtualX
      ? this.mother
      : this.father;
  }

  get leftNeighbour() {
    if (!this.family) return null;

    const sameGeneration = this.family.persons
      .filter((p) => p.rowDepth === this.rowDepth && p.virtualX < this.virtualX)
      .sort((a, b) => a.virtualX - b.virtualX);

    return sameGeneration.length > 0
      ? sameGeneration[sameGeneration.length - 1]
      : null;
  }

  get rightNeighbour() {
    if (!this.family) return null;

    const sameGeneration = this.family.persons
      .filter((p) => p.rowDepth === this.rowDepth && p.virtualX > this.virtualX)
      .sort((a, b) => a.virtualX - b.virtualX);

    return sameGeneration.length > 0 ? sameGeneration[0] : null;
  }

  get isParent() {
    return this.children.length > 0;
  }

  get isSingle() {
    return this.spouses.length === 0;
  }

  get hasParents() {
    return this.father !== null || this.mother !== null;
  }

  get hasChildren() {
    return this.children.length > 0;
  }

  get primarySpouse() {
    if (!this.primarySpouseId) return null;
    return this.family?.getPersonById(this.primarySpouseId);
  }

  get alternatePrimarySpouseInAbsenceOfRealPrimarySpouse() {
    if (this.spouses.length === 0) return null;

    if (this.primarySpouse) {
      return this.primarySpouse;
    } else if (this.spouses.length === 1) {
      return this.spouses[0];
    } else if (this.spouses.length > 0) {
      return this.spouses[0];
    }

    return null;
  }

  getAllChildrenWithSpouse(spouse) {
    if (this.isSingle) {
      return this.children.sort((a, b) => a.virtualX - b.virtualX);
    }

    spouse = spouse || this.alternatePrimarySpouseInAbsenceOfRealPrimarySpouse;
    if (!spouse) {
      return [];
    }

    const childrenWithPrimarySpouse = this.children
      .filter((c) => {
        return (
          (c.mother && c.mother.pid === spouse.pid) ||
          (c.father && c.father.pid === spouse.pid)
        );
      })
      .sort((a, b) => a.virtualX - b.virtualX);

    return childrenWithPrimarySpouse;
  }

  getLeftMostChildWithSpouse(spouse) {
    const children = this.getAllChildrenWithSpouse(spouse);
    return children.length > 0 ? children[0] : null;
  }

  getRightMostChildWithSpouse(spouse) {
    const children = this.getAllChildrenWithSpouse(spouse);
    return children.length > 0 ? children[children.length - 1] : null;
  }

  // (Removed dead getters actualSymbolWidth/actualSymbolHeight: they referenced
  // config keys that never existed — SYMBOL_WIDTH/HEIGHT, REDUCE_*_FOR_FETUS/
  // UNKNOWN_GENDER — so they returned NaN, and had zero callers repo-wide. Fetus
  // sizing is now done at render in Canvas.jsx nodeSymbolArea via FETUS_SYMBOL_SCALE.)

  setRowDepthWithoutCausingRecursion(depth) {
    this.rowDepth = depth;
  }

  getAllRelatedPeopleInSameGeneration(excludingPeople = []) {
    const relatedPeople = [];

    if (excludingPeople.includes(this)) {
      return relatedPeople;
    }

    relatedPeople.push(this);
    excludingPeople.push(this);

    // Add all siblings
    this.siblings.forEach((sibling) => {
      relatedPeople.push(
        ...sibling.getAllRelatedPeopleInSameGeneration(excludingPeople)
      );
    });

    // Add all spouses
    this.spouses.forEach((spouse) => {
      relatedPeople.push(
        ...spouse.getAllRelatedPeopleInSameGeneration(excludingPeople)
      );
    });

    // Return unique people
    return [...new Set(relatedPeople)];
  }

  addSpouseInOrder(spouse) {
    if (!this.spousesInOrder.find((s) => s.pid === spouse.pid)) {
      this.spousesInOrder.push(spouse);
    }
  }

  ancestorScore(previousPerson = null) {
    let score = 0;

    if (this.hasParents) {
      // Use parent's columnDepth as score
      if (this.father) {
        score += this.father.columnDepth || 0;
      } else if (this.mother) {
        score += this.mother.columnDepth || 0;
      }
    } else if (previousPerson === null && this.spouses.length > 0) {
      // Spouse without parents: inherit partner's ancestorScore
      // This groups them together with their spouse
      score += this.spouses[0].ancestorScore(this);
    }

    return score;
  }

  // for debug
  toString() {
    return `Person(pid=${this.pid}, name=${
      this.name
    }, x=${this.virtualX.toFixed(2)}, y=${this.virtualY})`;
  }
}
